import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["baseUri", "modelSelect"]
    static values = {
        modelsUrl: String,
        defaultModel: String,
        blackwellBaseUri: String,
        blackwellModel: String
    }

    connect() {
        if (!this.hasBaseUriTarget || !this.hasModelSelectTarget) return

        this.requestSequence = 0
        this.baseUriChanged()
    }

    async baseUriChanged() {
        const baseUri = this.normalizeBaseUri(this.baseUriTarget.value)

        if (this.blackwellSelected(baseUri)) {
            this.updateFixedBlackwellModel()
            return
        }

        const requestId = ++this.requestSequence
        const currentSelection = String(this.modelSelectTarget.value || "").trim()
        const models = await this.fetchModelsForBaseUri(baseUri)
        if (requestId !== this.requestSequence) return

        this.renderModelOptions(models, currentSelection)
    }

    renderModelOptions(models, currentSelection = "") {
        const effectiveModels = this.uniqueModels(models)
        if (effectiveModels.length === 0) {
            this.modelSelectTarget.innerHTML = '<option disabled selected>No models available</option>'
            this.modelSelectTarget.disabled = false
            return
        }

        const fragment = document.createDocumentFragment()
        effectiveModels.forEach((model) => {
            const option = document.createElement("option")
            option.value = model
            option.textContent = model
            fragment.appendChild(option)
        })
        this.modelSelectTarget.innerHTML = ""
        this.modelSelectTarget.appendChild(fragment)

        const fallbackModel = String(this.defaultModelValue || "").trim()
        const nextSelection = effectiveModels.includes(currentSelection)
            ? currentSelection
            : effectiveModels.includes(fallbackModel)
                ? fallbackModel
                : effectiveModels[0]
        this.modelSelectTarget.value = nextSelection
        this.modelSelectTarget.disabled = false
    }

    updateFixedBlackwellModel() {
        const model = String(
            this.blackwellModelValue ||
            this.defaultModelValue ||
            ""
        ).trim()
        if (model.length === 0) {
            this.renderModelOptions([], "")
            return
        }
        this.modelSelectTarget.innerHTML = `<option value="${model}">${model}</option>`
        this.modelSelectTarget.value = model
        this.modelSelectTarget.disabled = true
    }

    async fetchModelsForBaseUri(baseUri) {
        const modelsUrl = String(this.modelsUrlValue || "").trim()
        if (modelsUrl.length === 0) return []

        const requestUrl = `${modelsUrl}?base_uri=${encodeURIComponent(baseUri)}`
        try {
            const response = await fetch(requestUrl, {
                method: "GET",
                headers: { "Accept": "application/json" }
            })
            if (!response.ok) return []

            const payload = await response.json()
            return this.uniqueModels(payload?.models)
        } catch (_error) {
            return []
        }
    }

    uniqueModels(models) {
        return Array.from(
            new Set(
                Array.from(models || [])
                    .map((value) => String(value || "").trim())
                    .filter((value) => value.length > 0)
            )
        )
    }

    blackwellSelected(baseUri) {
        const normalizedBaseUri = this.normalizeBaseUri(baseUri)
        const blackwellBaseUri = this.normalizeBaseUri(this.blackwellBaseUriValue)
        if (normalizedBaseUri.length === 0 || blackwellBaseUri.length === 0) return false
        return normalizedBaseUri === blackwellBaseUri
    }

    normalizeBaseUri(value) {
        return String(value || "").trim().replace(/\/+$/, "")
    }
}
